import React, { useEffect, useState, useCallback, useRef, createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Volume, Chapter, EditorSelectionContext, PendingQuote, PendingQuoteRequest } from '../../types';
import type { ChapterStatus } from '@xiaotianshu/shared';
import { GlobalConfirmModal } from '../../components/modals/GlobalConfirmModal';
import { CreateChapterModal } from './components/Modals';
import { VolumeManagerModal } from './components/VolumeManagerModal';
import { useEditorContext } from '../Editor/context';
import { useChapterVersion } from './hooks/useChapterVersion';
import { debugLogger } from '../../utils/debugLogger';
import { Pencil, Trash2, FilePlus } from 'lucide-react';
import { dataCache } from '../../cache/DataCache';
import { numberToChinese as toChineseNumeral } from '../../utils/numberToChinese';
import { flattenOrderedChapterIds, pickChapterAfterDelete } from './pickChapterAfterDelete';

type ConfirmModalState = {
  show: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
} | null;

type ContextMenuState = {
  x: number;
  y: number;
  type: 'volume' | 'chapter';
  target: Volume | Chapter;
} | null;

interface WorkspaceContextValue {
  bookId: string;
  bookTitle: string;
  /**
   * 通用确认弹窗（工作台内共享一个 GlobalConfirmModal）。
   * 传 `{ show: true, title, message, onConfirm, onCancel? }` 打开；
   * `setConfirmModal(null)` 关闭。给"离开前确认"这类跨组件场景用。
   */
  setConfirmModal: (state: ConfirmModalState) => void;

  // 数据
  volumes: Volume[];
  chaptersMap: Record<string, Chapter[]>;
  expandedVolumes: string[];
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  selectedChapter: Chapter | null;
  setSelectedChapter: (chapter: Chapter) => void;
  chapterContent: string;
  isChapterContentLoading: boolean;
  filteredVolumes: Volume[];

  // 章节树操作
  toggleVolume: (volId: string) => void;
  handleSelectChapter: (chapter: Chapter) => Promise<void>;
  /** 更新章节写作状态 / 字数目标（字段级局部更新，不动正文） */
  handleUpdateChapterMeta: (chapterId: string, patch: { status?: ChapterStatus; targetWords?: number }) => Promise<void>;
  handleContextMenu: (e: React.MouseEvent, type: 'volume' | 'chapter', target: Volume | Chapter) => void;
  /** 卷目管理弹窗开关（创建/改名/删卷/调整章节归属与顺序） */
  setShowVolumeManager: (show: boolean, focusVolumeId?: string) => void;
  setShowChapterModal: (v: { volumeId: string; afterChapterNumber?: number } | null) => void;
  loadVolumes: () => Promise<void>;
  collapseAllVolumes: () => void;

  // 展示辅助
  numberToChinese: (num: number) => string;
  getVolumeIndex: (vol: Volume) => number;
  getChapterIndex: (chap: Chapter) => number;

  // 编辑器绑定
  editor: import('@tiptap/react').Editor | null;
  setEditorRef: (editor: import('@tiptap/react').Editor | null) => void;
  isExternalWriting: boolean;
  setHasUnsavedChanges: (v: boolean) => void;

  // 版本历史
  versionRefreshKey: number;
  handleOpenVersionHistory: () => void;
  handleSaveChapter: (title: string, summary: string, content: string) => Promise<void>;
  handleRestoreVersion: ReturnType<typeof useChapterVersion>['handleRestoreVersion'];
  handleDeleteVersion: (versionId: string) => void;
  handleVersionSaved: () => void;

  // 右键润色请求（ChapterEditor 右键 → AI 面板）
  polishRequest: { selection: EditorSelectionContext; mode: 'polish' | 'condense' | 'expand'; nonce: number } | null;
  handlePolishSelection: (selection: EditorSelectionContext, mode: 'polish' | 'condense' | 'expand') => void;
  // AI 助手引用（右键"添加到助手"的位置引用列表，仅存位置不存文本）
  assistantRefs: EditorSelectionContext[];
  handleAddToAssistant: (selection: EditorSelectionContext) => void;
  handleRemoveAssistantRef: (index: number) => void;
  handleClearAssistantRefs: () => void;

  // 章节删除（编辑器区使用）
  handleDeleteChapter: (volumeId: string, chapterId: string) => void;

  // AI 面板辅助
  handleAIAutoSaveRef: React.MutableRefObject<(() => Promise<void>) | null>;
  handleRequestChapterSwitch: (chapterId: string) => Promise<boolean>;
  setIsExternalWriting: (locked: boolean) => void;

  // 资料库刷新（AI 修改世界观数据后触发 DataLibrary / WorldSettings 重载）
  dataRefreshTrigger: number;
  triggerDataRefresh: () => void;

  // 伏笔引文定位（伏笔页点条目 → 跳回正文选中那段原文）
  pendingQuote: PendingQuote | null;
  jumpToQuote: (quote: PendingQuoteRequest) => Promise<void>;
  clearPendingQuote: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);
export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider');
  return ctx;
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { id } = useParams();
  const bookId = id || '00000000-0000-0000-0000-000000000000';
  const { setActiveView } = useEditorContext();

  function resolveInitialChapter(): { chapter: Chapter | null; content: string } {
    const cachedChapters = dataCache.getChaptersByVolumeRaw(bookId);
    if (!cachedChapters) return { chapter: null, content: '' };

    const cachedPrefs = dataCache.userPreferences;
    let lastChapterId: string | null = null;
    if (cachedPrefs) {
      const pref = cachedPrefs.find(p => p.preference_name === `lastChapter_${bookId}`);
      lastChapterId = pref?.preference_value ?? null;
    }

    let target: Chapter | null = null;
    if (lastChapterId) {
      const cid = lastChapterId;
      for (const chaps of Object.values(cachedChapters)) {
        const found = chaps.find(c => c.id === cid);
        if (found) { target = found; break; }
      }
    }

    if (!target) {
      const vols = dataCache.getVolumes(bookId) ?? [];
      const sortedVols = [...vols].sort((a, b) => a.order_index - b.order_index);
      for (let i = sortedVols.length - 1; i >= 0; i--) {
        const chaps = (cachedChapters[sortedVols[i].id] || []).sort((a, b) => a.chapter_number - b.chapter_number);
        if (chaps.length > 0) { target = chaps[chaps.length - 1]; break; }
      }
    }

    const content = target ? (dataCache.getChapterContent(target.id, bookId) ?? '') : '';
    return { chapter: target, content };
  }

  const initialCh = resolveInitialChapter();

  const [volumes, setVolumes] = useState<Volume[]>(() => dataCache.getVolumes(bookId) ?? []);
  const [bookTitle, setBookTitle] = useState(() => {
    const cached = dataCache.books?.find(x => x.id === bookId);
    return cached ? cached.title : '';
  });
  const [chaptersMap, setChaptersMap] = useState<Record<string, Chapter[]>>(() => {
    const cached = dataCache.getChaptersByVolumeRaw(bookId);
    return cached ?? {};
  });
  const [expandedVolumes, setExpandedVolumes] = useState<string[]>(() => {
    const vols = dataCache.getVolumes(bookId) ?? [];
    if (initialCh.chapter) {
      return [initialCh.chapter.volume_id];
    }
    return vols.length > 0 ? [vols[0].id] : [];
  });
  /** 卷目管理弹窗（卷的增删改 + 章节归属与顺序，集中在一处） */
  const [showVolumeManager, setShowVolumeManagerState] = useState(false);
  /** 打开卷目管理时要直接进入重命名的卷（右键菜单「编辑分卷」用），null 表示只打开列表 */
  const [volumeManagerFocus, setVolumeManagerFocus] = useState<string | null>(null);
  const setShowVolumeManager = useCallback((show: boolean, focusVolumeId?: string) => {
    setVolumeManagerFocus(show ? (focusVolumeId ?? null) : null);
    setShowVolumeManagerState(show);
  }, []);

  /**
   * 卷列表的最新值（ref 镜像）。
   *
   * 用途：`onSaved` 需要在 `loadVolumes()` **之后**立刻读到期盼的新卷列表来做差分，
   * 但 `volumes` 这个 state 在本轮闭包里仍是旧值（setState 要等下次渲染才生效）。
   */
  const volumesRef = useRef(volumes);
  volumesRef.current = volumes;

  /**
   * 首屏兜底：卷数据到位后，若一个卷都没展开，就展开第一个。
   *
   * 为什么放在这里而不是 `loadVolumes` 里：`loadVolumes` 会反复被调用（保存、AI 写入、
   * 跨端同步…），它用调用时的闭包值判断展开集合，会把"刚创建并展开的新卷"覆盖掉
   * （曾导致"新建第二卷后它显示为折叠"）。这里改为**只在一开始补一次**。
   */
  const initialExpandDoneRef = useRef(false);
  useEffect(() => {
    if (initialExpandDoneRef.current) return;
    if (volumes.length === 0) return;
    initialExpandDoneRef.current = true;
    setExpandedVolumes(prev => {
      if (prev.length > 0) return prev;
      return selectedChapter ? [selectedChapter.volume_id] : [volumes[0].id];
    });
    // 只在卷数据首次到位时执行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volumes]);
  const [showChapterModal, setShowChapterModal] = useState<{ volumeId: string; afterChapterNumber?: number } | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(initialCh.chapter);
  const [chapterContent, setChapterContent] = useState(initialCh.content);
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>(null);
  /**
   * 「删除章节」确认框的 resolve。
   *
   * 为什么需要它：`handleDeleteChapter` 只负责**弹确认框**，调用方无法得知用户最终确认了没有
   * （它不返回 Promise）。而卷目管理里删除章节必须知道结果 —— 否则草稿会先把章节抹掉，
   * 用户一取消就与真实数据不一致。`deleteChapterNow` 用这个 ref 把"用户的选择"变成 Promise。
   */
  const pendingChapterDeleteRef = useRef<((confirmed: boolean) => void) | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const [editorRef, setEditorRef] = useState<import('@tiptap/react').Editor | null>(null);
  const [isExternalWriting, setIsExternalWriting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isChapterContentLoading, setIsChapterContentLoading] = useState(false);
  const [dataRefreshTrigger, setDataRefreshTrigger] = useState(0);
  const triggerDataRefresh = useCallback(() => setDataRefreshTrigger(n => n + 1), []);

  const selectedChapterRef = useRef<Chapter | null>(initialCh.chapter);
  selectedChapterRef.current = selectedChapter;
  const chapterContentRef = useRef(initialCh.content);
  chapterContentRef.current = chapterContent;
  const manualNavRef = useRef(false);

  const [versionRefreshKeyState, setVersionRefreshKeyState] = useState(0);

  const {
    handleOpenVersionHistory,
    handleSaveChapter,
    handleRestoreVersion,
    handleDeleteVersion,
    handleVersionSaved,
  } = useChapterVersion({
    bookId,
    selectedChapter,
    chapterContent,
    setChapterContent,
    setSelectedChapter,
    setIsChapterContentLoading,
    loadVolumes: async () => { await loadVolumes(); },
    setVersionRefreshKey: () => setVersionRefreshKeyState(k => k + 1),
    setConfirmModal,
    setActiveView: (view: string) => setActiveView(view as 'chapters' | 'version-history'),
  });

  const loadVolumes = async () => {
    // 删除前的快照：用来判断"当前打开的那一章是不是刚被删掉了"，以及它前面还有哪些
    // 存活章节（见下面的 pickChapterAfterDelete 接线）。必须在覆盖 state 之前取。
    const orderedIdsBefore = flattenOrderedChapterIds(volumes, chaptersMap);
    const volumeIdsBefore = new Set(volumes.map(v => v.id));

    // 强制从服务端读取最新卷/章数据：AI 写库（execute）、跨端修改后需要即时反映，
    // 缓存（内存 + sessionStorage 快照）可能过期——直接在读取前失效再拉取。
    dataCache.invalidateVolumes(bookId);
    dataCache.invalidateChapters(bookId);
    const vols = await window.api.getVolumes(bookId);
    dataCache.setVolumes(bookId, vols);
    setVolumes(vols);

    const map: Record<string, Chapter[]> = {};
    const chaptersByVol = new Map<string, Chapter[]>();
    const chapResults = await Promise.all(
      vols.map(async (vol) => {
        const chaps = await window.api.getChapters(vol.id);
        return { volId: vol.id, chaps };
      }),
    );
    for (const { volId, chaps } of chapResults) {
      map[volId] = chaps;
      chaptersByVol.set(volId, chaps);
    }
    dataCache.setChaptersByVolume(bookId, chaptersByVol);
    setChaptersMap(map);
    // 注：这里**不再**改动展开状态。
    // 曾经是 `if (vols.length > 0 && expandedVolumes.length === 0) setExpandedVolumes([vols[0].id])` ——
    // 它用调用时的闭包值判断，于是"先展开新卷、再 refresh"会被这次 refresh 覆盖回第一卷
    // （表现为"通过卷目管理新建了第二卷，但它出来是折叠的"）。
    // 展开状态由创建入口显式维护（见 handleCreateVolume / handleCreateChapter）。

    if (!selectedChapter) {
      let lastChapterId: string | null = null;
      try {
        const pref = await window.api.getUserPreference(`lastChapter_${bookId}`);
        lastChapterId = pref?.preference_value ?? null;
      } catch { /* ignore */ }
      let targetChapter: Chapter | null = null;

      if (lastChapterId) {
        const chapterId = lastChapterId;
        for (const vol of vols) {
          const chaps = map[vol.id] || [];
          const found = chaps.find(c => c.id === chapterId);
          if (found) { targetChapter = found; break; }
        }
      }

      if (!targetChapter) {
        const sortedVols = [...vols].sort((a, b) => a.order_index - b.order_index);
        for (let i = sortedVols.length - 1; i >= 0; i--) {
          const chaps = (map[sortedVols[i].id] || []).sort((a, b) => a.chapter_number - b.chapter_number);
          if (chaps.length > 0) { targetChapter = chaps[chaps.length - 1]; break; }
        }
      }

      if (targetChapter) {
        const cachedContent = dataCache.getChapterContent(targetChapter.id, bookId);
        const content = cachedContent ?? await window.api.getChapterContent(targetChapter.id);
        if (!cachedContent) {
          const contentsMap = dataCache.getChapterContentsRaw(bookId) ?? new Map();
          contentsMap.set(targetChapter.id, content);
          dataCache.setChapterContents(bookId, contentsMap);
        }
        setSelectedChapter(targetChapter);
        setChapterContent(content);
        const targetVolumeId = targetChapter.volume_id;
        if (!expandedVolumes.includes(targetVolumeId)) {
          setExpandedVolumes(prev => [...prev, targetVolumeId]);
        }
      }
    } else if (
      // 当前正在编辑的章节已不在新列表里（被删了）……
      !Object.values(map).flat().some(c => c.id === selectedChapter.id)
      // ……而且它**确实**在"删除前列表"里，才认定是"本次刷新把它删掉了"
      && orderedIdsBefore.includes(selectedChapter.id)
      // 前后还有共同的卷 → 还是同一个作品。（换了作品时旧章节当然也不在新列表里，
      // 但那不是"被删除"，不该由这里接管选章。）
      && vols.some(v => volumeIdsBefore.has(v.id))
    ) {
      // ── 当前正在编辑的章节被删掉了 ─────────────────────────────
      // 用户实测：让 AI 删掉当前这一章后，章节列表刷新了，但编辑器里**仍显示已删除
      // 章节的正文**（标题也是旧的）—— 因为上面那个分支只在 `!selectedChapter` 时挑章节，
      // 而 selectedChapter 还指着那个已经不存在的对象，chapterContent 自然也是旧的。
      // 现在按"删除前的顺序"补选：前一个存活者 → 后一个存活者 → 空状态。
      const aliveChapterIds = new Set(Object.values(map).flat().map(c => c.id));
      const deletedChapterIds = new Set(orderedIdsBefore.filter(id => !aliveChapterIds.has(id)));
      const nextId = pickChapterAfterDelete(orderedIdsBefore, deletedChapterIds, selectedChapter.id);
      const nextChapter = nextId
        ? Object.values(map).flat().find(c => c.id === nextId) ?? null
        : null;

      if (nextChapter) {
        // 走 switchToChapter：正文（缓存已被上面 invalidateChapters 失效，必定重新拉取）、
        // "上次阅读位置"偏好、展开卷、未保存标记一次性同步，编辑器随之重建。
        setExpandedVolumes(prev => (prev.includes(nextChapter.volume_id) ? prev : [...prev, nextChapter.volume_id]));
        await switchToChapter(nextChapter);
      } else {
        // 整本书被删空：给空状态（Workspace 的 selectedChapter 分支），
        // 绝不能把已删除章节的正文留在编辑器里。
        setSelectedChapter(null);
        setChapterContent('');
        setHasUnsavedChanges(false);
      }
    }
  };

  useEffect(() => { loadVolumes(); }, [bookId, dataRefreshTrigger]);

  // 跨设备/浏览器同步：刷新（快照启动）后向服务端校验"上次阅读位置"并校正，
  // 用户手动切换章节后不再干预
  useEffect(() => {
    let cancelled = false;
    manualNavRef.current = false;
    (async () => {
      let serverId: string | null = null;
      try {
        const pref = await window.api.getUserPreference(`lastChapter_${bookId}`);
        serverId = pref?.preference_value ?? null;
      } catch { /* 网络异常时保持本地状态 */ }
      if (cancelled || manualNavRef.current) return;

      const current = selectedChapterRef.current;
      if (serverId && current && current.id !== serverId) {
        const cached = dataCache.getChaptersByVolumeRaw(bookId);
        const target = cached
          ? Object.values(cached).flat().find(c => c.id === serverId) ?? null
          : null;
        if (target) {
          await switchToChapter(target);
          return;
        }
      }

      // 服务端无记录（或记录已失效）：当前章节若未预载正文则补拉
      if (current && chapterContentRef.current === '') {
        setIsChapterContentLoading(true);
        try {
          const content = await window.api.getChapterContent(current.id);
          if (!cancelled) {
            const contentsMap = dataCache.getChapterContentsRaw(bookId) ?? new Map();
            contentsMap.set(current.id, content);
            dataCache.setChapterContents(bookId, contentsMap);
            setChapterContent(content);
          }
        } catch { /* ignore */ }
        if (!cancelled) setIsChapterContentLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  useEffect(() => {
    if (!bookId) return;
    const cached = dataCache.books?.find(x => x.id === bookId);
    if (cached) {
      setBookTitle(cached.title);
      return;
    }
    let cancelled = false;
    window.api.getBooks().then(books => {
      if (cancelled) return;
      if (!dataCache.books) dataCache.books = books;
      const b = books.find(x => x.id === bookId);
      if (b) setBookTitle(b.title);
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [bookId]);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const toggleVolume = (volId: string) => {
    if (expandedVolumes.includes(volId)) setExpandedVolumes(expandedVolumes.filter(id => id !== volId));
    else setExpandedVolumes([...expandedVolumes, volId]);
  };

  const collapseAllVolumes = useCallback(() => {
    setExpandedVolumes([]);
  }, []);

  // 注：卷的新建/重命名/删除/顺序、章节归属与顺序，统一由「卷目管理」的草稿模式提交
  // （见 VolumeManagerModal 与 POST /books/:id/structure）。
  // 这里不再保留逐个操作的处理器 —— 它们无法完成"重编号"，会留下编号断档。
  // 新建卷也走那条路（弹窗内先 createVolume 拿到 id，再随 chapters 一起提交），
  // 新卷的"自动展开"由挂载弹窗处的 onSaved 用卷列表差分处理。

  /**
   * 删除单个章节并**等待用户确认**（卷目管理用）。
   *
   * 与 `handleDeleteChapter` 的区别：后者只弹确认框、不返回结果；这里返回 Promise，
   * 由确认框的 resolve 决定 —— 只有用户真的确认了才返回 true。
   */
  const deleteChapterNow = async (chapterId: string): Promise<boolean> => {
    const confirmed = await new Promise<boolean>((resolve) => {
      pendingChapterDeleteRef.current = resolve;
      setConfirmModal({
        show: true,
        title: '删除章节',
        message: '确定删除该章节吗？此操作将永久移除内容，不可撤销。',
        onConfirm: () => {
          pendingChapterDeleteRef.current = null;
          resolve(true);
          setConfirmModal(null);
        },
        onCancel: () => {
          pendingChapterDeleteRef.current = null;
          resolve(false);
          setConfirmModal(null);
        },
      });
    });
    if (!confirmed) return false;

    await window.api.deleteChapter(chapterId);
    // 删除后重新拉一遍，避免与卷目草稿的其它改动互相踩
    await loadVolumes();
    triggerDataRefresh();
    return true;
  };

  const handleCreateChapter = async (title: string, summary: string, content: string) => {
    if (!showChapterModal) return;
    const targetVolumeId = showChapterModal.volumeId;
    await window.api.createChapter({
      volumeId: showChapterModal.volumeId,
      afterChapterNumber: showChapterModal.afterChapterNumber,
      title, summary, content,
    });
    await loadVolumes();
    // 展开目标卷：卷表头上的 ＋ 在**折叠状态**下依然可点，若不展开，
    // 用户加完一章却看不到它（新章被折在卷里），会以为没建成功。
    setExpandedVolumes(prev => (prev.includes(targetVolumeId) ? prev : [...prev, targetVolumeId]));
    triggerDataRefresh();
    const volumeId = showChapterModal.volumeId;
    setShowChapterModal(null);
    const chaps = await window.api.getChapters(volumeId);
    const sortedChaps = [...chaps].sort((a, b) => a.chapter_number - b.chapter_number);
    if (sortedChaps.length > 0) {
      await handleSelectChapter(sortedChaps[sortedChaps.length - 1]);
    }
  };

  const handleDeleteChapter = async (volumeId: string, chapterId: string) => {
    setConfirmModal({
      show: true,
      title: '删除章节',
      message: '确定删除该章节吗？此操作将永久移除内容。',
      onConfirm: async () => {
        await window.api.deleteChapter(chapterId);
        const vols = await window.api.getVolumes(bookId);
        const map: Record<string, Chapter[]> = {};
        for (const vol of vols) {
          const chaps = await window.api.getChapters(vol.id);
          map[vol.id] = chaps;
        }
        setVolumes(vols);
        setChaptersMap(map);

        const sortedVols = [...vols].sort((a, b) => a.order_index - b.order_index);
        let nextChapter: Chapter | null = null;
        for (const v of sortedVols) {
          const chaps = (map[v.id] || []).sort((a, b) => a.chapter_number - b.chapter_number);
          if (chaps.length > 0) { nextChapter = chaps[0]; break; }
        }

        if (nextChapter) await handleSelectChapter(nextChapter);
        else { setSelectedChapter(null); setChapterContent(''); }
        triggerDataRefresh();
        setConfirmModal(null);
      }
    });
  };

  const switchToChapter = async (chapter: Chapter) => {
    setSelectedChapter(chapter);
    void window.api.setUserPreference(`lastChapter_${bookId}`, String(chapter.id)).catch(() => { /* ignore */ });
    const cachedContent = dataCache.getChapterContent(chapter.id, bookId);
    if (!cachedContent) setIsChapterContentLoading(true);
    const content = cachedContent ?? await window.api.getChapterContent(chapter.id);
    if (!cachedContent) {
      const contentsMap = dataCache.getChapterContentsRaw(bookId) ?? new Map();
      contentsMap.set(chapter.id, content);
      dataCache.setChapterContents(bookId, contentsMap);
    }
    setChapterContent(content);
    setIsChapterContentLoading(false);
    setHasUnsavedChanges(false);
    setActiveView('chapters');
  };

  /**
   * 更新章节的写作状态/字数目标（局部更新）。
   * 走 PUT /chapters/:id 的字段级部分更新，不会触碰正文——此前该接口
   * 无条件覆盖 title/summary/content，只改状态会把正文清空。
   */
  const handleUpdateChapterMeta = useCallback(async (
    chapterId: string,
    patch: { status?: ChapterStatus; targetWords?: number },
  ): Promise<void> => {
    await window.api.updateChapter({ id: chapterId, ...patch });
    // 就地更新内存中的两份元数据，避免为一次状态切换重拉全部章节
    setSelectedChapter(prev => (prev && prev.id === chapterId ? { ...prev, ...patch } : prev));
    setChaptersMap(prev => {
      const next: Record<string, Chapter[]> = {};
      for (const [volId, list] of Object.entries(prev)) {
        next[volId] = list.map(c => (c.id === chapterId ? { ...c, ...patch } : c));
      }
      return next;
    });
    triggerDataRefresh();
    // 显式列出 setSelectedChapter：它虽由 useState 提供、引用恒定，但 React Compiler 会把
    // "读了它却不在依赖里"判为"不可保留的 memoization"，这是 lint **error**（不是 warning）。
  }, [triggerDataRefresh, setSelectedChapter]);

  const handleSelectChapter = async (chapter: Chapter) => {
    manualNavRef.current = true;
    setActiveView('chapters');
    if (hasUnsavedChanges && selectedChapter && selectedChapter.id !== chapter.id) {
      const target = chapter;
      setConfirmModal({
        show: true,
        title: '未保存的变动',
        message: `当前章节《${selectedChapter.title}》有未保存的变动，是否保存后再切换？`,
        confirmText: '保存并切换',
        cancelText: '不保存',
        onConfirm: async () => {
          if (editorRef) {
            await handleSaveChapter(selectedChapter.title, selectedChapter.summary || '', editorRef.getHTML());
          }
          setConfirmModal(null);
          switchToChapter(target);
        },
        onCancel: () => {
          setConfirmModal(null);
          switchToChapter(target);
        },
      });
      return;
    }
    switchToChapter(chapter);
  };

  const handleRequestChapterSwitch = useCallback(async (chapterId: string): Promise<boolean> => {
    manualNavRef.current = true;
    setActiveView('chapters'); // AI 面板跳转也要切回章节视图，保证侧边栏高亮
    try {
      const vols = await window.api.getVolumes(bookId);
      for (const vol of vols) {
        const chaps = await window.api.getChapters(vol.id);
        const target = chaps.find(c => c.id === chapterId);
        if (target) {
          if (!expandedVolumes.includes(target.volume_id)) {
            setExpandedVolumes(prev => [...prev, target.volume_id]);
          }
          setSelectedChapter(target);
          void window.api.setUserPreference(`lastChapter_${bookId}`, String(target.id)).catch(() => { /* ignore */ });
          const cachedContent = dataCache.getChapterContent(target.id, bookId);
          const content = cachedContent ?? await window.api.getChapterContent(target.id);
          if (!cachedContent) {
            const contentsMap = dataCache.getChapterContentsRaw(bookId) ?? new Map();
            contentsMap.set(target.id, content);
            dataCache.setChapterContents(bookId, contentsMap);
          }
          setChapterContent(content);
          return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  }, [bookId, expandedVolumes]);

  const handleAIAutoSaveRef = useRef<(() => Promise<void>) | null>(null);

  const handleAIAutoSave = useCallback(async () => {
    if (editorRef && selectedChapter) {
      try {
        const meta = await window.api.getChapterMetadata(selectedChapter.id);
        const title = meta?.title ?? selectedChapter.title;
        const summary = meta?.summary ?? selectedChapter.summary;
        debugLogger.system('AI_AUTO_SAVE_TRIGGERED', { chapterId: selectedChapter.id, title, summaryLen: summary?.length ?? 0, selectedChapterTitle: selectedChapter.title });
        await handleSaveChapter(title, summary || '', editorRef.getHTML());
      } catch {
        // AI auto-save failure is non-critical
      }
    }
  }, [editorRef, selectedChapter, handleSaveChapter]);

  handleAIAutoSaveRef.current = handleAIAutoSave;

  /** 章节元数据（含发布状态）变化后刷新本地状态与缓存：
   *  1) 更新 selectedChapter，编辑器立即同步最新状态；
   *  2) 就地更新 dataCache（章节树对象引用同步变新）；
   *  3) 重写 sessionStorage 快照，刷新页面不再恢复旧状态；
   *  4) 重载章节树（新对象引用），避免点击项闭包持有旧对象。 */
  // 右键润色请求：转发给 EditorAIPanel（AIAssistantPanel 监听后进入润色对话）
  const [polishRequest, setPolishRequest] = useState<{ selection: EditorSelectionContext; mode: 'polish' | 'condense' | 'expand'; nonce: number } | null>(null);
  const handlePolishSelection = useCallback((selection: EditorSelectionContext, mode: 'polish' | 'condense' | 'expand') => {
    setPolishRequest({ selection, mode, nonce: Date.now() });
  }, []);

  // AI 助手引用：右键"添加到助手"（仅存位置，AI 自行查询定位）
  const [assistantRefs, setAssistantRefs] = useState<EditorSelectionContext[]>([]);
  const handleAddToAssistant = useCallback((selection: EditorSelectionContext) => {
    setAssistantRefs(prev => {
      if (prev.some(r => r.chapterId === selection.chapterId && r.from === selection.from && r.to === selection.to)) return prev;
      return [...prev, selection];
    });
  }, []);
  const handleRemoveAssistantRef = useCallback((index: number) => {
    setAssistantRefs(prev => prev.filter((_, i) => i !== index));
  }, []);
  const handleClearAssistantRefs = useCallback(() => {
    setAssistantRefs([]);
  }, []);

  /**
   * 伏笔引文定位：伏笔页点"埋于"引用 → 切到该章 → 选中/高亮当初标记的那段原文。
   *
   * setActiveView 不在这里做 —— 它由 handleRequestChapterSwitch 负责切回章节视图，
   * 这里只负责把"要定位的引文"投递出去（编辑器组件消费后自行清理）。
   */
  const [pendingQuote, setPendingQuote] = useState<PendingQuote | null>(null);
  const quoteNonceRef = useRef(0);
  const jumpToQuote = useCallback(async (quote: PendingQuoteRequest) => {
    quoteNonceRef.current += 1;
    setPendingQuote({ ...quote, nonce: quoteNonceRef.current });
    await handleRequestChapterSwitch(quote.chapterId);
  }, [handleRequestChapterSwitch]);
  const clearPendingQuote = useCallback(() => setPendingQuote(null), []);

  const numberToChinese = (num: number): string => toChineseNumeral(num);

  const getVolumeIndex = (vol: Volume) => {
    const sorted = [...volumes].sort((a, b) => a.order_index - b.order_index);
    return sorted.findIndex(v => v.id === vol.id) + 1;
  };

  const getChapterIndex = (chap: Chapter) => {
    return chap.chapter_number;
  };

  const filteredVolumes = volumes.filter(vol => {
    if (!searchQuery) return true;
    const volMatch = vol.title.toLowerCase().includes(searchQuery.toLowerCase());
    const chapters = chaptersMap[vol.id] || [];
    return volMatch || chapters.some(c => c.title.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const handleContextMenu = (e: React.MouseEvent, type: 'volume' | 'chapter', target: Volume | Chapter) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, type, target });
  };

  const value: WorkspaceContextValue = {
    bookId,
    bookTitle,
    // 通用确认弹窗：面板（如"AI 会话进行中还想退出对话"）也需要它，
    // 否则每个调用方都得自己再挂一个 GlobalConfirmModal。
    setConfirmModal,
    volumes,
    chaptersMap,
    expandedVolumes,
    searchQuery,
    setSearchQuery,
    selectedChapter,
    setSelectedChapter,
    chapterContent,
    isChapterContentLoading,
    filteredVolumes,
    toggleVolume,
    handleSelectChapter,
    handleContextMenu,
    setShowVolumeManager,
    setShowChapterModal,
    loadVolumes,
    collapseAllVolumes,
    numberToChinese,
    getVolumeIndex,
    getChapterIndex,
    editor: editorRef,
    setEditorRef,
    isExternalWriting,
    setHasUnsavedChanges,
    versionRefreshKey: versionRefreshKeyState,
    handleOpenVersionHistory,
    handleSaveChapter,
    handleRestoreVersion,
    handleDeleteVersion,
    handleVersionSaved,
    polishRequest,
    handlePolishSelection,
    assistantRefs,
    handleAddToAssistant,
    handleRemoveAssistantRef,
    handleClearAssistantRefs,
    handleDeleteChapter,
    handleAIAutoSaveRef,
    handleRequestChapterSwitch,
    setIsExternalWriting,
    dataRefreshTrigger,
    triggerDataRefresh,
    handleUpdateChapterMeta,
    pendingQuote,
    jumpToQuote,
    clearPendingQuote,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}

      {showVolumeManager && (
        <VolumeManagerModal
          bookId={bookId}
          volumes={volumes}
          chaptersMap={chaptersMap}
          focusVolumeId={volumeManagerFocus}
          onClose={() => setShowVolumeManager(false)}
          onSaved={async () => {
            // 本次提交新增了哪些卷（用保存前的卷列表做差分）。
            // 必须在 loadVolumes 之前取快照 —— loadVolumes 会就地更新 volumes。
            const before = volumesRef.current.map(v => v.id);
            await loadVolumes();
            const createdIds = volumesRef.current.map(v => v.id).filter(id => !before.includes(id));
            // 新卷是空的：展开它们，用户才能看到"该分卷暂无章节"并往里加章
            if (createdIds.length > 0) {
              setExpandedVolumes(prev => [...prev, ...createdIds.filter(id => !prev.includes(id))]);
            }
            triggerDataRefresh();
            // 重编号后当前章节的章号可能变了，重新取一次元数据保持一致。
            // 单独 try/catch：这只是"顺手刷新"，失败不该让「保存卷目」整体报错
            // （否则用户会以为结构没保存成功，从而重复操作）。
            if (selectedChapter) {
              try {
                const fresh = await window.api.getChapterMetadata(selectedChapter.id);
                if (fresh) setSelectedChapter(fresh);
              } catch {
                /* 元数据刷新失败不影响"结构已保存"这一事实 */
              }
            }
          }}
          // 卷目管理里删章节要**等到用户确认**才知道结果，故用返回 Promise 的版本
          onDeleteChapter={async (chapter: Chapter) => { await deleteChapterNow(chapter.id); }}
        />
      )}
      {showChapterModal !== null && (
        <CreateChapterModal onClose={() => setShowChapterModal(null)} onSave={handleCreateChapter} />
      )}
      {confirmModal && (
        <GlobalConfirmModal
          show={confirmModal.show}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel ?? (() => setConfirmModal(null))}
          confirmText={confirmModal.confirmText ?? '确定'}
          cancelText={confirmModal.cancelText ?? '取消'}
        />
      )}

      {contextMenu && (
        <div className="context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
          {contextMenu.type === 'volume' && (
            <>
              <button onClick={() => { setShowVolumeManager(true, (contextMenu.target as Volume).id); setContextMenu(null); }}>
                <Pencil size={14} /> 编辑分卷
              </button>
              <button onClick={() => { setShowChapterModal({ volumeId: (contextMenu.target as Volume).id }); setContextMenu(null); }}>
                <FilePlus size={14} /> 新建章节
              </button>
              {/* 卷的删除统一走「卷目管理」：那里能选择"章节转移到哪一卷 / 连同章节删除"，
                  而右键直接删只能二选一（旧实现是硬编码转移到上一卷）。
                  默认卷连入口都不给（服务端也拒绝）——它通常装着全书。 */}
              {!(contextMenu.target as Volume).is_default && (
                <>
                  <div className="context-menu-divider" />
                  <button className="context-menu-danger" onClick={() => { setShowVolumeManager(true, (contextMenu.target as Volume).id); setContextMenu(null); }}>
                    <Trash2 size={14} /> 删除分卷
                  </button>
                </>
              )}
            </>
          )}
          {contextMenu.type === 'chapter' && (
            <>
              <button onClick={() => { handleSelectChapter(contextMenu.target as Chapter); setContextMenu(null); }}>
                <Pencil size={14} /> 编辑章节
              </button>
              <button onClick={() => { const chap = contextMenu.target as Chapter; setShowChapterModal({ volumeId: chap.volume_id, afterChapterNumber: chap.chapter_number }); setContextMenu(null); }}>
                <FilePlus size={14} /> 在此插入章节
              </button>
              <div className="context-menu-divider" />
              <button className="context-menu-danger" onClick={() => { const chap = contextMenu.target as Chapter; handleDeleteChapter(chap.volume_id, chap.id); setContextMenu(null); }}>
                <Trash2 size={14} /> 删除章节
              </button>
            </>
          )}
        </div>
      )}
    </WorkspaceContext.Provider>
  );
}
